import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import notificationService from 'shared/services/notificationService';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';
import { timeAgo } from 'features/alerts/format';
import TableFooter from 'shared/components/tables/TableFooter';
import { isNumericTeamId } from 'features/agent-analytics/annotations/annotationDeepLink';

function safeParseJson(maybeJson) {
  if (!maybeJson) return {};
  if (typeof maybeJson === 'object') return maybeJson;
  if (typeof maybeJson !== 'string') return {};
  try {
    return JSON.parse(maybeJson);
  } catch (_) {
    return {};
  }
}

function getExplicitMetaSummary(metaObj) {
  const s =
    metaObj?.summary ||
    metaObj?.summaryText ||
    metaObj?.summary_text ||
    metaObj?.shortSummary ||
    null;
  if (typeof s === 'string' && s.trim()) return s.trim();
  return '';
}

function getListSummary(metaObj, message) {
  const explicit = getExplicitMetaSummary(metaObj);
  if (explicit) return explicit;

  const msg = typeof message === 'string' ? message : '';
  const cleaned = msg.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.length > 160 ? `${cleaned.slice(0, 157)}...` : cleaned;
}

function notifyUnreadRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('trodo:inbox-unread-refresh'));
  }
}

function annotationInboxPath(teamSlug, metaObj) {
  if (!teamSlug || isNumericTeamId(teamSlug) || metaObj?.kind !== 'annotation') return null;
  const team = encodeURIComponent(String(teamSlug));
  const qs = new URLSearchParams({ tab: 'annotations' });
  if (metaObj.span_id) {
    qs.set('span_id', String(metaObj.span_id));
    if (metaObj.span_started_at) qs.set('span_started_at', String(metaObj.span_started_at));
    return `/${team}/agent-analytics/traces?${qs.toString()}`;
  }
  if (metaObj.run_id) {
    qs.set('run_id', String(metaObj.run_id));
    return `/${team}/agent-analytics/runs?${qs.toString()}`;
  }
  if (metaObj.conversation_id) {
    qs.set('conversation_id', String(metaObj.conversation_id));
    return `/${team}/agent-analytics/conversations?${qs.toString()}`;
  }
  return null;
}

function inboxCtaHasTeamSlug(pathAndSearch) {
  const raw = String(pathAndSearch || '');
  const path = raw.split('?')[0];
  const first = decodeURIComponent(path.split('/').filter(Boolean)[0] || '');
  return Boolean(first) && !isNumericTeamId(first);
}

function resolveInboxCta(ctaUrl, metaObj, teamSlug) {
  if (typeof ctaUrl === 'string' && ctaUrl.startsWith('/') && inboxCtaHasTeamSlug(ctaUrl)) {
    return ctaUrl;
  }
  if (typeof ctaUrl === 'string' && ctaUrl && !ctaUrl.startsWith('/')) {
    try {
      const u = new URL(ctaUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const path = `${u.pathname}${u.search}`;
      if (inboxCtaHasTeamSlug(path)) return path;
    } catch {
      /* fall through to current-team rebuild */
    }
  }
  return annotationInboxPath(teamSlug, metaObj) || '';
}


/** The contextual tag for a notification; unknown types get their name, humanised, or nothing. */
export function notificationTag(item) {
  const kind = safeParseJson(item?.meta)?.kind;
  if (kind === 'annotation') return 'Annotation';
  if (kind === 'deprecated_models') return 'Model';
  switch (item?.type) {
    case 'usage': return 'Usage';
    case 'billing':
    case 'limits_billing': return 'Billing';
    case 'invite':
    case 'member': return 'Team';
    case 'updates': return 'Update';
    default: {
      const t = String(item?.type || '').replace(/[_-]+/g, ' ').trim();
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
    }
  }
}

function getMetaImages(m) {
  const v = m?.images || m?.imageUrls || m?.image_urls || m?.media?.images || m?.media?.imageUrls || [];
  return Array.isArray(v) ? v.filter((u) => typeof u === 'string' && u) : [];
}

function getMetaVideoUrl(m) {
  return m?.videoUrl || m?.video_url || m?.video || m?.media?.videoUrl || m?.media?.video_url || m?.media?.video || null;
}

const PAGE_SIZE = 20;
const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
];

const tagClass = 'inline-flex h-5 shrink-0 items-center rounded-full border border-[color:var(--border-default)] px-2 text-[11px] text-[color:var(--text-muted)]';
const primaryClass = 'inline-flex h-8 items-center justify-center rounded-md bg-brand px-4 text-[13px] font-medium text-[color:var(--text-on-brand)] transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

function useNarrow() {
  const query = '(max-width: 767px)';
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);
  return narrow;
}

/**
 * The user's inbox: the list on the left, the selected notification in full on
 * the right. Selecting only reads; the action button is what navigates.
 */
const InboxPage = () => {
  const { team } = useParams();
  const navigate = useNavigate();
  const narrow = useNarrow();
  useBreadcrumb([{ label: 'Inbox' }]);

  const [filter, setFilter] = useState('all');
  const unreadOnly = filter === 'unread';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const listRef = useRef(null);

  const syncUnread = useCallback((n) => {
    if (typeof n === 'number') {
      setUnread(n);
      notifyUnreadRefresh();
    }
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    setSelectedId(null);
    setDismissed(false);
    notificationService
      .getInbox({ unreadOnly, limit: PAGE_SIZE, page: 1 })
      .then((data) => {
        if (!live) return;
        const list = Array.isArray(data?.notifications) ? data.notifications : [];
        setItems(list);
        setTotal(typeof data?.total === 'number' ? data.total : list.length);
        setPage(1);
        if (typeof data?.unread === 'number') setUnread(data.unread);
      })
      .catch((err) => { if (live) setError(err?.response?.data?.message || 'Failed to load notifications'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [unreadOnly]);

  // `total` counts every notification, so with the Unread filter the page is
  // done when a short page comes back.
  const hasMore = unreadOnly ? items.length > 0 && items.length % PAGE_SIZE === 0 : items.length < total;

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await notificationService.getInbox({ unreadOnly, limit: PAGE_SIZE, page: page + 1 });
      const next = Array.isArray(data?.notifications) ? data.notifications : [];
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...next.filter((n) => !seen.has(n.id))];
      });
      setPage((p) => p + 1);
      if (typeof data?.unread === 'number') setUnread(data.unread);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, page, unreadOnly]);

  const markAllRead = useCallback(async () => {
    try {
      const res = await notificationService.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      syncUnread(typeof res?.unread === 'number' ? res.unread : 0);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to mark all as read');
    }
  }, [syncUnread]);

  const select = useCallback(
    async (item) => {
      setSelectedId(item.id);
      setDismissed(false);
      if (!item.read_at) {
        try {
          const res = await notificationService.markRead([item.id]);
          setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n)));
          if (typeof res?.unread === 'number') syncUnread(res.unread);
        } catch (err) {
          setError(err?.response?.data?.message || 'Failed to mark notification as read');
        }
      }
    },
    [syncUnread]
  );

  // Wide screens open on the newest notification (shown, not marked read).
  const effectiveId = selectedId ?? (!narrow && !dismissed && items.length ? items[0].id : null);
  const selected = useMemo(() => items.find((n) => n.id === effectiveId) || null, [items, effectiveId]);

  const onListKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const rows = Array.from(listRef.current?.querySelectorAll('button[data-row]') || []);
    const i = rows.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    rows[Math.max(0, Math.min(rows.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
  };

  const showList = !narrow || !selected;
  const showDetail = !narrow || !!selected;
  const pane = 'min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface';

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex min-h-0 flex-1 gap-3 p-3 sm:px-4">
        {showList && (
          <section className={`${pane} flex flex-1 md:w-[380px] md:flex-none lg:w-[420px]`} aria-label="Notifications">
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[color:var(--border-default)] p-2">
              <SegmentedTabs options={FILTERS} value={filter} onChange={setFilter} ariaLabel="Filter notifications" />
              <div className="flex-1" />
              <button type="button" onClick={markAllRead} disabled={unread <= 0} className={activityNeutralButtonClass}>
                Mark all read
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              {error && (
                <div role="alert" className="m-2 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger">
                  {error}
                </div>
              )}
              {loading ? (
                <PageLoader label="Loading notifications" />
              ) : items.length === 0 ? (
                !error && (
                  <div className="flex h-48 items-center justify-center px-6 text-center text-[12px] text-[color:var(--text-muted)]">
                    {unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
                  </div>
                )
              ) : (
                <ul ref={listRef} onKeyDown={onListKeyDown}>
                  {items.map((item) => (
                    <NotificationRow key={item.id} item={item} active={item.id === effectiveId} onSelect={select} />
                  ))}
                </ul>
              )}
              {!loading && (
                <TableFooter className="px-3 pb-2" shown={items.length} total={unreadOnly ? null : total} hasMore={hasMore} loading={loadingMore} onLoadMore={loadMore} />
              )}
            </div>
          </section>
        )}
        {showDetail && (
          <section className={`${pane} flex flex-1`} aria-label="Notification">
            {narrow && selected && (
              <div className="shrink-0 border-b border-[color:var(--border-default)] p-2">
                <button
                  type="button"
                  onClick={() => { setSelectedId(null); setDismissed(true); }}
                  className={`${activityNeutralButtonClass} gap-1 px-3`}
                >
                  <ChevronLeft size={14} aria-hidden /> Back
                </button>
              </div>
            )}
            {selected ? (
              <NotificationDetail item={selected} team={team} navigate={navigate} />
            ) : (
              <div className="flex flex-1 items-center justify-center px-6 text-center text-[13px] text-[color:var(--text-muted)]">
                {loading ? '' : 'Select a notification to read it.'}
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
};

function NotificationRow({ item, active, onSelect }) {
  const isUnread = !item.read_at;
  const summary = getListSummary(safeParseJson(item.meta), item.message);
  const tag = notificationTag(item);
  return (
    <li className="border-b border-[color:var(--border-subtle)] last:border-b-0">
      <button
        type="button"
        data-row
        onClick={() => onSelect(item)}
        aria-current={active ? 'true' : undefined}
        className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)] ${active ? 'bg-[color:var(--surface-hover)]' : ''}`}
      >
        <span aria-hidden className={`mt-[7px] inline-block h-2 w-2 shrink-0 rounded-full ${isUnread ? 'bg-brand' : ''}`} />
        {isUnread && <span className="sr-only">Unread:</span>}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={`min-w-0 flex-1 truncate text-[13px] text-[color:var(--text-primary)] ${isUnread ? 'font-semibold' : 'font-medium'}`}>
              {item.title}
            </span>
            <span className="shrink-0 whitespace-nowrap text-[12px] tabular-nums text-[color:var(--text-muted)]">{timeAgo(item.sent_at)}</span>
          </span>
          {summary ? <span className="mt-0.5 block truncate text-[12px] text-[color:var(--text-muted)]">{summary}</span> : null}
          {tag ? <span className={`${tagClass} mt-1.5`}>{tag}</span> : null}
        </span>
      </button>
    </li>
  );
}

function NotificationDetail({ item, team, navigate }) {
  const meta = safeParseJson(item.meta);
  const tag = notificationTag(item);
  const summary = getExplicitMetaSummary(meta);
  const images = getMetaImages(meta);
  const videoUrl = getMetaVideoUrl(meta);
  const cta = resolveInboxCta(item.cta_url, meta, team);
  const when = item.sent_at ? new Date(item.sent_at).toLocaleString() : '';
  const go = () => {
    if (cta.startsWith('/')) navigate(cta);
    else window.open(cta, '_blank', 'noopener,noreferrer');
  };
  return (
    <article className="min-h-0 flex-1 overflow-auto p-4 sm:p-6">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-2 text-[12px] text-[color:var(--text-muted)]">
          {tag ? <span className={tagClass}>{tag}</span> : null}
          {when ? <time dateTime={item.sent_at}>{when}</time> : null}
        </div>
        <h1 className="mt-3 text-[16px] font-semibold text-[color:var(--text-primary)]">{item.title}</h1>
        {item.message ? (
          <p className="mt-3 whitespace-pre-wrap text-[13px] leading-relaxed text-[color:var(--text-primary)]">{item.message}</p>
        ) : null}
        {summary ? (
          <div className="mt-4 rounded-md border border-[color:var(--border-default)] p-3">
            <div className="text-[12px] text-[color:var(--text-muted)]">Summary</div>
            <p className="mt-1 whitespace-pre-wrap text-[13px] text-[color:var(--text-primary)]">{summary}</p>
          </div>
        ) : null}
        {videoUrl ? (
          <video controls src={videoUrl} className="mt-4 w-full rounded-md border border-[color:var(--border-default)]" />
        ) : null}
        {images.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 gap-2">
            {images.slice(0, 5).map((url, idx) => (
              <img key={`${url}-${idx}`} src={url} alt="" className="w-full rounded-md border border-[color:var(--border-default)]" style={{ maxHeight: 320, objectFit: 'cover' }} />
            ))}
          </div>
        ) : null}
        {cta ? (
          <div className="mt-6">
            <button type="button" onClick={go} className={primaryClass}>{item.cta_text || 'Open'}</button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default InboxPage;
